import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { postResetPassword } from "../../helpers/eirna_backend_helper";
import eirnaLogo from "../../assets/images/eirna/eirnabio-connect-logo.png";
import LoginCarousel from "./LoginCarousel";

const ResetPasswordPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [token, setToken] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Extract token from URL query params: /reset-password?token=<token>
    const params = new URLSearchParams(location.search);
    const urlToken = params.get("token");

    if (!urlToken) {
      setError(
        "Invalid or missing reset token. Please request a new password reset link."
      );
    } else {
      setToken(urlToken);
    }
  }, [location.search]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: "",
      password_confirm: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("Please enter a new password"),
      password_confirm: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Please confirm your new password"),
    }),
    onSubmit: async (values) => {
      if (!token) {
        setError("Missing reset token. Please request a new password reset link.");
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response = await postResetPassword(token, {
          password: values.password,
          password_confirm: values.password_confirm,
        });

        if (response) {
          setSuccess(true);
          // Redirect to login after a short delay
          setTimeout(() => {
            navigate("/login");
          }, 3000);
        }
      } catch (err) {
        const errorMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to reset password. The link may have expired.";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  document.title = "Reset Password | EIRNABio Connect";

  return (
    <React.Fragment>
      <div
        style={{
          width: "100vw",
          minHeight: "100vh",
          margin: 0,
          padding: 0,
          overflow: "auto",
        }}
      >
        <Row className="g-0" style={{ minHeight: "100vh", margin: 0 }}>
          {/* Left Panel - Carousel Section */}
          <Col
            xl={6}
            lg={6}
            md={6}
            style={{
              padding: "0",
              position: "relative",
              minHeight: "100vh",
            }}
          >
            <div
              style={{
                width: "100%",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LoginCarousel />
            </div>
          </Col>

          {/* Right Panel - Reset Password Form Section */}
          <Col
            xl={6}
            lg={6}
            md={6}
            style={{
              backgroundColor: "white",
              minHeight: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "500px",
                padding: "40px",
              }}
            >
              {/* EIRNABio Logo Section */}
              <div className="text-center mb-4">
                <Link to="/" className="d-inline-block auth-logo">
                  <img
                    src={eirnaLogo}
                    alt="EIRNABio Connect"
                    height="80"
                    style={{ marginBottom: "20px" }}
                  />
                </Link>
              </div>

              {/* Welcome Text */}
              <div className="text-center mb-4">
                <h5
                  style={{
                    fontWeight: "600",
                    fontSize: "24px",
                    color: "#333",
                    marginBottom: "8px",
                  }}
                >
                  Set New Password
                </h5>
                <p
                  style={{
                    color: "#666",
                    fontSize: "14px",
                    margin: "0",
                  }}
                >
                  Enter your new password below
                </p>
              </div>

              {/* Success Alert */}
              {success && (
                <Alert color="success" className="mb-4">
                  <i className="mdi mdi-check-circle me-2"></i>
                  Your password has been reset successfully. Redirecting you to
                  login...
                </Alert>
              )}

              {/* Error Alert */}
              {error && (
                <Alert color="danger" className="mb-4">
                  <i className="mdi mdi-block-helper me-2"></i>
                  {error}
                </Alert>
              )}

              {!success && (
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}
                >
                  {/* New Password Field */}
                  <div className="mb-3">
                    <Label
                      htmlFor="password"
                      style={{
                        fontWeight: "500",
                        color: "#333",
                        marginBottom: "8px",
                        display: "block",
                      }}
                    >
                      New Password
                    </Label>
                    <div style={{ position: "relative" }}>
                      <Input
                        name="password"
                        placeholder="Enter your new password"
                        type={showPassword ? "text" : "password"}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.password || ""}
                        invalid={
                          validation.touched.password &&
                          validation.errors.password
                            ? true
                            : false
                        }
                        style={{
                          padding: "12px 44px 12px 16px",
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          fontSize: "14px",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#999",
                          padding: "0",
                          display: "flex",
                          alignItems: "center",
                        }}
                        tabIndex={-1}
                      >
                        <i
                          className={`mdi ${
                            showPassword ? "mdi-eye-off" : "mdi-eye"
                          }`}
                          style={{ fontSize: "18px" }}
                        />
                      </button>
                    </div>
                    {validation.touched.password && validation.errors.password ? (
                      <div
                        style={{
                          color: "#dc3545",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {validation.errors.password}
                      </div>
                    ) : null}
                  </div>

                  {/* Confirm Password Field */}
                  <div className="mb-3">
                    <Label
                      htmlFor="password_confirm"
                      style={{
                        fontWeight: "500",
                        color: "#333",
                        marginBottom: "8px",
                        display: "block",
                      }}
                    >
                      Confirm New Password
                    </Label>
                    <div style={{ position: "relative" }}>
                      <Input
                        name="password_confirm"
                        placeholder="Confirm your new password"
                        type={showConfirmPassword ? "text" : "password"}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.password_confirm || ""}
                        invalid={
                          validation.touched.password_confirm &&
                          validation.errors.password_confirm
                            ? true
                            : false
                        }
                        style={{
                          padding: "12px 44px 12px 16px",
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          fontSize: "14px",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#999",
                          padding: "0",
                          display: "flex",
                          alignItems: "center",
                        }}
                        tabIndex={-1}
                      >
                        <i
                          className={`mdi ${
                            showConfirmPassword ? "mdi-eye-off" : "mdi-eye"
                          }`}
                          style={{ fontSize: "18px" }}
                        />
                      </button>
                    </div>
                    {validation.touched.password_confirm &&
                    validation.errors.password_confirm ? (
                      <div
                        style={{
                          color: "#dc3545",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {validation.errors.password_confirm}
                      </div>
                    ) : null}
                  </div>

                  {/* Submit Button */}
                  <Button
                    color="success"
                    disabled={loading || !token}
                    className="w-100"
                    type="submit"
                    style={{
                      backgroundColor: "#0A5F3E",
                      borderColor: "#0A5F3E",
                      padding: "12px",
                      fontSize: "16px",
                      fontWeight: "500",
                      borderRadius: "8px",
                      border: "none",
                      marginTop: "16px",
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2">
                          Loading...
                        </Spinner>
                        Resetting Password...
                      </>
                    ) : (
                      "Reset Password"
                    )}
                  </Button>
                </Form>
              )}

              {/* Back to Login Section */}
              <div className="text-center mt-4">
                <p style={{ fontSize: "14px", color: "#666", margin: "0" }}>
                  Remember your password?{" "}
                  <Link
                    to="/login"
                    style={{
                      color: "#0A5F3E",
                      fontWeight: "500",
                      textDecoration: "none",
                    }}
                  >
                    Back to Login
                  </Link>
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </React.Fragment>
  );
};

export default ResetPasswordPage;